import { Button } from '@timelyapp/react';
import classNames from 'classnames';
import StackTraceModalLoader from 'components/dev-tools/StackTraceModalLoader';
import { CodeFile, Copy } from 'components/Icons';
import Label from 'components/Label';
import { Container } from 'components/ui-blocks/container';
import React from 'react';
import { useDispatch } from 'react-redux';
import { setShowStackTraceModal } from 'redux/modules/development';
import { getIsUserTimelyEmployee } from 'redux/selectors/user';
import type { ThunkDispatch } from 'types/redux';
import copyToClipboard from 'utils/copy-to-clipboard';
import { composeIntercomMessage } from 'utils/intercom-helper';
import type { CaughtReactError } from './ErrorBoundary';
import ErrorBoundary from './ErrorBoundary';
import styles from './PageErrorBoundary.module.scss';

interface PageErrorProps extends CaughtReactError {
  /** @default true */
  hasNavbar?: boolean;
}

export const PageError = ({ info, error, hasNavbar = true }: PageErrorProps) => {
  const errorString = error.toString();
  const copyableError = `
    ${errorString}

    Stacktrace: ${info.componentStack}
  `;

  let canSeeDecodeButton: boolean;
  try {
    canSeeDecodeButton = getIsUserTimelyEmployee.resultFunc(window.BOOTSTRAPPED_DATA.user.email);
  } catch {
    canSeeDecodeButton = false;
  }

  const dispatch = useDispatch<ThunkDispatch>();

  return (
    <Container alignContent="center" className={styles.container}>
      <div className={classNames(styles.errorContainer, { [styles.hasNavbar]: hasNavbar })}>
        <div className={styles.mainMessage}>
          <h2>Sorry, something went wrong</h2>
          <p>
            We've automatically logged the error, and will resolve this ASAP. In the mean time...
          </p>
        </div>
        <div className={styles.widget}>
          <Label variant="muted-info">Step 1</Label>
          <div className={styles.splitGrid}>
            <div>
              <h4>RELOAD TIMELY</h4>
              <p>
                Reloading Timely, or even <a href="/logout">logging out</a> and signing back in
                often resolves the issue.
              </p>
            </div>
            <div>
              <Button priority="xhigh" onClick={() => window.location.reload()}>
                Reload now
              </Button>
            </div>
          </div>
        </div>

        <div className={styles.widget}>
          <Label variant="muted-info">Step 2</Label>
          <div className={styles.splitGrid}>
            <div>
              <h4>CONTACT SUPPORT:</h4>
              <p>If this problem persists, get in touch with us!</p>
              <p>Please include the errors below, and what you were doing when this happened.</p>
            </div>
            <div>
              <Button
                priority="xhigh"
                onClick={() =>
                  composeIntercomMessage({
                    subject: `I having a problem: ${errorString}`,
                    body: info.componentStack,
                  })
                }
              >
                Contact support now
              </Button>
            </div>
          </div>
        </div>
        <div className={styles.widget}>
          <div className={styles.splitGrid}>
            <div>
              <h4>DEBUG INFORMATION:</h4>
            </div>
            <div className={styles.debugButtons}>
              <Button className={styles.iconButton} onClick={() => copyToClipboard(copyableError)}>
                <Copy /> Copy error to clipboard
              </Button>
              {canSeeDecodeButton && (
                <Button
                  className={styles.iconButton}
                  onClick={() => {
                    dispatch(setShowStackTraceModal([true, copyableError]));
                  }}
                >
                  <CodeFile /> Decode stack trace
                </Button>
              )}
            </div>
          </div>
          <br />
          <div>
            <strong>Error:</strong>
            <pre className={styles.unintendedCodeError}>{errorString}</pre>
          </div>
          <div>
            <strong>Stacktrace:</strong>
            <pre className={styles.codeError}>{info.componentStack}</pre>
          </div>
        </div>
      </div>
      <StackTraceModalLoader hasNavbar={hasNavbar} />
    </Container>
  );
};

interface Props {
  children?: React.ReactNode;
}

export function PageErrorBoundary(props: Props) {
  return (
    <ErrorBoundary FallbackComponent={PageError} catchInDev>
      {props.children}
    </ErrorBoundary>
  );
}
